import { c as _c } from "react/compiler-runtime";
import React, { useId } from "react";

/**
 * Small inline flags for the licence countries. SVG rather than emoji: Windows
 * renders flag emoji as plain letters, and Kurdistan has no flag emoji at all.
 * Simplified for ~20px — recognisable, not heraldically exact.
 */

const W = 30;
const H = 20;
function Stripes(t0) {
  const $ = _c(9);
  const {
    colors,
    vertical: t1
  } = t0;
  const vertical = t1 === undefined ? false : t1;
  const size = (vertical ? W : H) / colors.length;
  let t2;
  if ($[0] !== colors || $[1] !== size || $[2] !== vertical) {
    let t3;
    if ($[4] !== size || $[5] !== vertical) {
      t3 = (color, i) => vertical ? <rect key={color + i} x={i * size} y={0} width={size} height={H} fill={color} /> : <rect key={color + i} x={0} y={i * size} width={W} height={size} fill={color} />;
      $[4] = size;
      $[5] = vertical;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = colors.map(t3);
    $[0] = colors;
    $[1] = size;
    $[2] = vertical;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <>{t2}</>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

/** Kurdistan: red, white, green with the golden 21-ray sun. */
function Kurdistan() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = Array.from({
      length: 21
    }, _temp);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const rays = t0.join("");
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <><Stripes colors={["#ED2024", "#FFFFFF", "#278E43"]} /><path d={rays} fill="#FEBD11" /><circle cx={15} cy={10} r={2.6} fill="#FEBD11" /></>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function _temp(_, i) {
  const a = i / 21 * Math.PI * 2 - Math.PI / 2;
  const b = a + Math.PI / 21;
  const c = a - Math.PI / 21;
  const [cx, cy, r, R] = [15, 10, 2.4, 5];
  return `M${cx + Math.cos(c) * r},${cy + Math.sin(c) * r}L${cx + Math.cos(a) * R},${cy + Math.sin(a) * R}L${cx + Math.cos(b) * r},${cy + Math.sin(b) * r}Z`;
}
function UnitedStates() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <>{Array.from({
        length: 13
      }, _temp2)}<rect x={0} y={0} width={12} height={7 * H / 13} fill="#3C3B6E" />{[2, 5, 8].flatMap(_temp3)}</>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/** Iran: green, white, red with the red emblem. */
function _temp3(y, row) {
  return (row % 2 ? [3.5, 7.5] : [2, 6, 10]).map(x => <circle key={`${x}-${y}`} cx={x} cy={y} r={0.7} fill="#FFFFFF" />);
}
function _temp2(_, i) {
  return <rect key={i} x={0} y={i * H / 13} width={W} height={H / 13 + 0.05} fill={i % 2 ? "#FFFFFF" : "#B22234"} />;
}
function Iran() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <><Stripes colors={["#239F40", "#FFFFFF", "#DA0000"]} /><path d="M15 7.2c-.9.8-1.2 1.8-.8 2.9.2.6.5 1 .8 1.3.3-.3.6-.7.8-1.3.4-1.1.1-2.1-.8-2.9zm-1.9 1c-.9.9-1 2.3-.2 3.3.4.5 1 .8 1.6.9-.9-.7-1.4-1.6-1.4-2.6 0-.6 0-1.1 0-1.6zm3.8 0c0 .5 0 1 0 1.6 0 1-.5 1.9-1.4 2.6.6-.1 1.2-.4 1.6-.9.8-1 .7-2.4-.2-3.3z" fill="#DA0000" /></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
const FLAGS: Record<string, () => React.ReactElement> = {
  IQ: Kurdistan,
  DE: () => <Stripes colors={["#000000", "#DD0000", "#FFCE00"]} />,
  US: UnitedStates,
  FR: () => <Stripes colors={["#002395", "#FFFFFF", "#ED2939"]} vertical />,
  IR: Iran
};
export default function CountryFlag(t0) {
  const $ = _c(17);
  const {
    country,
    className: t1
  } = t0;
  const className = t1 === undefined ? "w-[22px] h-[15px]" : t1;
  const clip = useId();
  const Flag = FLAGS[country];
  if (!Flag) {
    const t2 = `${className} text-current`;
    let t3;
    let t4;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <circle cx="12" cy="12" r="9" />;
      t4 = <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9M12 3C9.5 5.6 8.2 8.6 8.2 12s1.3 6.4 3.8 9" strokeLinecap="round" />;
      $[0] = t3;
      $[1] = t4;
    } else {
      t3 = $[0];
      t4 = $[1];
    }
    let t5;
    if ($[2] !== t2) {
      t5 = <svg className={t2} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden={true}>{t3}{t4}</svg>;
      $[2] = t2;
      $[3] = t5;
    } else {
      t5 = $[3];
    }
    return t5;
  }
  const t2 = `${className} shrink-0`;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <rect width={W} height={H} rx={3} />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== clip) {
    t4 = <defs><clipPath id={clip}>{t3}</clipPath></defs>;
    $[5] = clip;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = `url(#${clip})`;
  let t6;
  if ($[7] !== Flag) {
    t6 = <Flag />;
    $[7] = Flag;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <g clipPath={t5}>{t6}</g>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <rect x={0.25} y={0.25} width={W - 0.5} height={H - 0.5} rx={2.8} fill="none" stroke="#000" strokeOpacity={0.12} strokeWidth={0.5} />;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t2 || $[14] !== t4 || $[15] !== t7) {
    t9 = <svg className={t2} viewBox={`0 0 ${W} ${H}`} aria-hidden={true}>{t4}{t7}{t8}</svg>;
    $[13] = t2;
    $[14] = t4;
    $[15] = t7;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
}